import { z } from "zod";

// Labels as campaigns and platforms write them (`link_in_bio`, `video-a`):
// no spaces, addresses or free text, so a label cannot carry personal data.
const label = z
  .string()
  .trim()
  .toLowerCase()
  .min(1)
  .max(200)
  .regex(/^[a-z0-9]+(?:[-_.][a-z0-9]+)*$/);
const host = z
  .string()
  .trim()
  .toLowerCase()
  .min(1)
  .max(253)
  .regex(/^[a-z0-9]+(?:[-.][a-z0-9]+)*$/);

export const SALE_IN_APP_BROWSERS = [
  "instagram",
  "tiktok",
  "facebook",
  "snapchat",
  "other",
] as const;
export type InAppBrowser = (typeof SALE_IN_APP_BROWSERS)[number];

// Ordered most-specific first. The "other" bucket catches the remaining
// webview signals (LINE, WeChat) without a label of their own.
export const IN_APP_BROWSER_PATTERNS: ReadonlyArray<{
  source: InAppBrowser;
  pattern: RegExp;
}> = [
  { source: "instagram", pattern: /instagram/i },
  { source: "tiktok", pattern: /tiktok|musical_ly|bytedance/i },
  { source: "facebook", pattern: /fbav|fban|fbios|fb_iab/i },
  { source: "snapchat", pattern: /snapchat/i },
  { source: "other", pattern: /line\/|micromessenger/i },
];

/**
 * The social app whose in-app browser a user agent belongs to, or null for a
 * regular browser. The user agent can be spoofed: a hint, not ground truth.
 */
export function detectInAppBrowser(ua: string): InAppBrowser | null {
  for (const { source, pattern } of IN_APP_BROWSER_PATTERNS) {
    if (pattern.test(ua)) return source;
  }
  return null;
}

/**
 * Where a sale came from, for Lissen's own reporting: the landing's campaign
 * labels, the site that referred it and the in-app browser it happened in.
 * Never the landing URL, a click id or a browser identity. It is kept with or
 * without advertising consent; nothing in it is sent to an ad provider.
 */
export const saleSourceSchema = z.object({
  source: label.optional(),
  medium: label.optional(),
  campaign: label.optional(),
  content: label.optional(),
  referrer: host.optional(),
  inApp: z.enum(SALE_IN_APP_BROWSERS).optional(),
  /** The buyer's advertising-consent choice when the purchase started. */
  adConsent: z.enum(["granted", "denied", "unset"]).optional(),
});
export type SaleSource = z.infer<typeof saleSourceSchema>;

/** One landing that says where the visitor came from, and when. */
export const saleTouchSchema = saleSourceSchema
  .pick({
    source: true,
    medium: true,
    campaign: true,
    content: true,
    referrer: true,
  })
  .extend({ at: z.number().finite() });
export type SaleTouch = z.infer<typeof saleTouchSchema>;

export const saleTouchesSchema = z.object({
  paid: saleTouchSchema.optional(),
  latest: saleTouchSchema.optional(),
});
export type SaleTouches = z.infer<typeof saleTouchesSchema>;

const UTM = {
  source: "utm_source",
  medium: "utm_medium",
  campaign: "utm_campaign",
  content: "utm_content",
} as const;

/**
 * What a page view says about where the visitor came from: its campaign
 * labels and the other site that linked to it. Undefined when it says
 * nothing (an internal navigation, a typed address). A label that is
 * repeated or malformed is left out; the rest are kept.
 */
export function saleTouchFromPage(page: {
  search: string;
  referrer: string;
  /** The page's own host: a referrer on it is not another site. */
  host: string;
}): Omit<SaleTouch, "at"> | undefined {
  const params = new URLSearchParams(page.search);
  const touch: Omit<SaleTouch, "at"> = {};
  for (const [key, param] of Object.entries(UTM) as Array<
    [keyof typeof UTM, string]
  >) {
    const values = params.getAll(param);
    const parsed = values.length === 1 ? label.safeParse(values[0]) : null;
    if (parsed?.success) touch[key] = parsed.data;
  }
  let referrer: string | undefined;
  try {
    referrer = new URL(page.referrer).hostname.replace(/^www\./, "");
  } catch {
    /* No referrer, or an analytics placeholder such as `$direct`. */
  }
  const own = page.host.toLowerCase().replace(/^www\./, "");
  const parsed = referrer && referrer !== own ? host.safeParse(referrer) : null;
  if (parsed?.success) touch.referrer = parsed.data;
  return Object.keys(touch).length > 0 ? touch : undefined;
}

function labelled(touch: Omit<SaleTouch, "at">) {
  return !!(touch.source || touch.medium || touch.campaign || touch.content);
}

function paid(touch: Omit<SaleTouch, "at">) {
  return (
    touch.medium === "paid" &&
    (touch.source === "meta" || touch.source === "tiktok")
  );
}

/**
 * Keep the touches a sale is later read from. A landing with campaign labels
 * replaces the latest one. A landing with only a referrer counts when nothing
 * is held yet: coming back from sign-in or the bank is not a new source.
 */
export function noteSaleTouch(
  held: SaleTouches,
  touch: SaleTouch,
): SaleTouches {
  if (!labelled(touch)) return held.latest ? held : { ...held, latest: touch };
  return { paid: paid(touch) ? touch : held.paid, latest: touch };
}

export const SALE_PAID_WINDOW_MS = 24 * 60 * 60 * 1000;

/**
 * The source of a sale made at `at`: a paid touch in the 24 hours before it
 * wins, otherwise the latest known source.
 */
export function saleSourceAt(
  held: SaleTouches,
  at: number,
  context: Pick<SaleSource, "inApp" | "adConsent"> = {},
): SaleSource {
  const recent =
    held.paid && held.paid.at <= at && at - held.paid.at < SALE_PAID_WINDOW_MS
      ? held.paid
      : undefined;
  const { at: _at, ...touch } = recent ?? held.latest ?? { at };
  return {
    ...touch,
    ...(context.inApp ? { inApp: context.inApp } : {}),
    ...(context.adConsent ? { adConsent: context.adConsent } : {}),
  };
}

export const SALE_CHANNELS = [
  "paid_meta",
  "paid_tiktok",
  "instagram",
  "tiktok",
  "facebook",
  "lissen_app",
  "other",
  "direct",
] as const;
export type SaleChannel = (typeof SALE_CHANNELS)[number];

function from(source: SaleSource, names: string[], domains: string[]) {
  return (
    names.includes(source.source ?? "") ||
    domains.some(
      (domain) =>
        source.referrer === domain || source.referrer?.endsWith(`.${domain}`),
    )
  );
}

/**
 * The one channel a sale is reported under. `direct` covers a sale whose
 * source says nothing and one made before sources were kept. A purchase made
 * in the Lissen app is `lissen_app`; the caller knows that from the
 * purchase's origin, not from a sale source.
 */
export function saleChannel(source: SaleSource | null | undefined): SaleChannel {
  if (!source) return "direct";
  if (source.medium === "paid" && source.source === "meta") return "paid_meta";
  if (source.medium === "paid" && source.source === "tiktok")
    return "paid_tiktok";
  // Instagram labels only some profile-link taps (`ig` / `link_in_bio`), so
  // a labelled and an unlabelled Instagram landing are one channel.
  if (from(source, ["ig", "instagram"], ["instagram.com"])) return "instagram";
  if (from(source, ["tiktok"], ["tiktok.com"])) return "tiktok";
  if (from(source, ["fb", "facebook"], ["facebook.com", "fb.com"]))
    return "facebook";
  if (labelled(source) || source.referrer) return "other";
  // No label and no referrer: the in-app browser is the only sign left.
  if (
    source.inApp === "instagram" ||
    source.inApp === "tiktok" ||
    source.inApp === "facebook"
  )
    return source.inApp;
  return source.inApp ? "other" : "direct";
}

const METADATA = {
  source: "src_source",
  medium: "src_medium",
  campaign: "src_campaign",
  content: "src_content",
  referrer: "src_referrer",
  inApp: "src_in_app",
  adConsent: "src_ad_consent",
} as const satisfies Record<keyof SaleSource, string>;

/** Flat payment metadata, as `adContextMetadata` carries the ad context. */
export function saleSourceMetadata(
  source?: SaleSource | null,
): Record<string, string> {
  if (!source) return {};
  return Object.fromEntries(
    (Object.entries(METADATA) as Array<[keyof SaleSource, string]>).flatMap(
      ([key, name]) => (source[key] ? [[name, source[key]]] : []),
    ),
  );
}

/** Null when the payment carried no sale source at all. */
export function saleSourceFromMetadata(
  metadata: Record<string, unknown>,
): SaleSource | null {
  const input = Object.fromEntries(
    (Object.entries(METADATA) as Array<[keyof SaleSource, string]>).flatMap(
      ([key, name]) => (name in metadata ? [[key, metadata[name]]] : []),
    ),
  );
  if (Object.keys(input).length === 0) return null;
  const parsed = saleSourceSchema.safeParse(input);
  return parsed.success ? parsed.data : null;
}
